import { trackWarn } from '../../tracking/warns';

/**
 * Performs final validation before we submit a new Contact to the API, to ensure that we have
 * provided fields that are absolutely required.
 */
/* eslint-disable complexity */
export const validateCreateContact = (formData = {}) => {
  if (!formData) {
    return;
  }
  const action = 'createContact';
  const fullName = formData.data?.name?.full_name;
  const phone = formData.data?.contact_details?.phone;
  const countryCode = formData.data?.location?.country_code;
  const zipCode = formData.data.location?.zip_code;
  const message = formData.data?.message;
  const referer = formData.data?.referer;
  const domain = window.location?.hostname;

  if (!fullName) {
    alertMissing(action, 'full_name');
    throw new Error('Full name is required');
  } else {
    alertFormatting(action, 'full_name', fullName, regex('full_name'));
  }

  if (!phone) {
    alertMissing(action, 'phone');
    throw new Error('Phone number is required');
  }

  if (phone && countryCode == 'US') {
    alertFormatting(action, 'us_phone', phone, regex('us_phone'));
  } else if (phone) {
    alertFormatting(action, 'intl_phone', phone, regex('intl_phone'));
  }

  if (zipCode && countryCode == 'US') {
    alertFormatting(action, 'us_zip_code', zipCode, regex('us_zip_code'));
  } else if (zipCode && countryCode == 'CA') {
    alertFormatting(action, 'ca_zip_code', zipCode, regex('ca_zip_code'));
  } else if (zipCode && countryCode == 'GB') {
    alertFormatting(action, 'gb_zip_code', zipCode, regex('gb_zip_code'));
  } else if (zipCode) {
    alertFormatting(action, 'intl_zip_code', zipCode, regex('intl_zip_code'));
  }

  alertController(action,'full_name', fullName);
  alertController(action,'referer', formData.data?.referer);
  alertController(action,'landing_page', formData.data?.landing_page);
  alertController(action,'page', formData.data?.page);
  alertController(action,'visitor_id', formData.data?.visitor_id);

  // field value alerting
  if (message) {
    alertMessage(action, formatMessage(message));
  }

  // UT specific validation rules
  if (domain?.includes('ut.dev')) {
    alertController(action,'ut_dev_referer', referer);
  } else if (domain?.includes('utstaging')) {
    alertController(action,'ut_st_referer', referer);
  } else if (domain?.includes('universitytutor')) {
    alertController(action,'ut_pr_referer', referer);
  }
};
/* eslint-enable complexity */

/**
 * Performs final validation before we submit a new Tutor applicant to the API,
 * to ensure that we have provided fields that are absolutely required.
 */
/* eslint-disable complexity */
export const validateCreateTutor = (formData = {}) => {
  if(!formData) return;
  const action = 'createTutor';

  const email = formData.email;
  if(!email) {
    alertMissing(action, 'email');
    throw new Error('Email is required');
  }

  const firstName = formData.first_name;
  if(!firstName) {
    alertMissing(action, 'first_name');
    throw new Error('First name is required');
  } else {
    alertFormatting(action, 'first_name', firstName, regex('first_name'));
  }

  const lastName = formData.last_name;
  if(!lastName) {
    alertMissing(action, 'last_name');
    throw new Error('Last name is required');
  } else {
    alertFormatting(action, 'last_name', lastName, regex('last_name'));
  }

  const phoneNumber = formData.phone_number;
  if(!phoneNumber) {
    alertMissing(action, 'phone_number');
    throw new Error('Phone number is required');
  }

  const tutorSummaryNote = formData.tutor_summary_note;
  if(!tutorSummaryNote) {
    alertMissing(action, 'tutor_summary_note');
    throw new Error('Tutor details are required');
  }

  const zipCode = formData.zip_code;
  const countryCode = formData.country_code;
  if (zipCode && countryCode == 'US') {
    alertFormatting(action, 'us_zip_code', zipCode, regex('us_zip_code'));
  } else if (zipCode && countryCode == 'CA') {
    alertFormatting(action, 'ca_zip_code', zipCode, regex('ca_zip_code'));
  } else if (zipCode && countryCode == 'GB') {
    alertFormatting(action, 'gb_zip_code', zipCode, regex('gb_zip_code'));
  } else if (zipCode) {
    alertFormatting(action, 'intl_zip_code', zipCode, regex('intl_zip_code'));
  } else {
    alertMissing(action, 'zip_code');
    throw new Error('Invalid zip code');
  }

  const landingPage = formData.landing_page;

  alertController(action, 'email', email);
  alertController(action, 'landing_page', landingPage);
  alertController(action, 'phone_number', phoneNumber);
  alertController(action, 'tutor_summary_note', tutorSummaryNote);
  alertController(action, 'zip_code', zipCode);

  const notes = {};
  tutorSummaryNote.split(' | ').forEach(value => {
    let splitValue = value.split(': ');
    notes[splitValue[0]]= splitValue[1];
  });

  alertController(action, 'referer', notes['referer']);
  alertController(action, 'visitor_id', notes['visitor_id']);
};
/* eslint-enable complexity */

/**
 * Performs final validation before we submit a new User to the API, to ensure that we have
 * provided fields that are absolutely required.
 */
export const validateCreateUser = (formData = {}) => {
   if (!formData) {
     return;
   }
  const action = 'createUser';
  const firstName = formData.first_name;
  const lastName = formData.last_name;
  const email = formData.email;
  const password = formData.password;
  const message = formData.message;

  if (!firstName) {
    alertMissing(action, 'first_name');
    throw new Error('Full name is required');
  }

  if (!lastName) {
    alertMissing(action, 'last_name');
    throw new Error('Full name is required');
  }

  if (!email) {
    alertMissing(action, 'email');
    throw new Error('Email is required');
  }

  if (!password) {
    alertMissing(action, 'password');
    throw new Error('Password is required');
  }

  alertController(action, 'first_name', firstName);
  alertController(action, 'last_name', lastName);
  alertController(action, 'email', email);
  alertController(action, 'password', password);
  alertController(action, 'referer', formData.cookies?.referer);
  alertController(action, 'landing_page', formData.cookies?.landing_page);
  alertController(action, 'page', formData.cookies?.page);
  alertController(action, 'visitor_id', formData.cookies?.visitor_id);

  // field value alerting
  if (message) {
    alertMessage(action, formatMessage(message));
  }
};

/**
 * Parses the message sting and returns a hash
 */
 export const formatMessage = (message) => {
  const fields = message.split(' | ');
  const fieldObject = {};

  fields.forEach(element => {
    const fieldName = element.split(': ')[0];
    const fieldValue = element.split(': ')[1];
    fieldObject[fieldName] = fieldValue;
  });
  return fieldObject;
};

/**
 * Alerts when specific fields have incorrect values
 */
function checkMatchAndAlert (action, validValues, field, fieldValues) {
  let matches = false;
  validValues.forEach(value => {
    if (fieldValues.toUpperCase().includes(value.toUpperCase())) {
      matches = true;
    }
  });

  if (!matches) {
    alertFieldValue(action, field, fieldValues);
  }
}

 export const alertMessage = (action, fields) => {
  const howSoon = fields['how_soon'];
  const whoNeeds = fields['who_needs_help'];
  const grade = fields['grade_level'];

  if (howSoon) {
    const validValues = ['Right away', 'In a few weeks', 'Not sure', 'Other', 'Within a few days', 'Within a few weeks'];
    checkMatchAndAlert(action, validValues, 'how_soon', howSoon);
  }

  if (whoNeeds) {
    const validValues = ['I do', 'Myself','My child', 'Someone else'];
    checkMatchAndAlert(action, validValues, 'who_needs_help', whoNeeds);
  }

  if (grade) {
    const validValues = [ 'Preschool',
                          'Kindergarten',
                          'Elementary',
                          'Elementary/Primary School',
                          '1st grade',
                          '2nd grade',
                          '3rd grade',
                          '4th grade',
                          '5th grade',
                          'Middle School',
                          '6th grade',
                          '7th grade',
                          '8th grade',
                          'High School',
                          '9th grade',
                          '10th grade',
                          '11th grade',
                          '12th grade',
                          'College or Graduate',
                          'College',
                          'Graduate Level',
                          'University Level',
                          'Adult Learner',
                          'Adult / Professional',
                          'Other' ];
    checkMatchAndAlert(action, validValues, 'grade_level', grade);
  }
};

/**
 * Controller to determine missing or formatting validation
 */
export const alertController = (action, key, value) => {
  if (!value) {
    alertMissing(action, key);
  } else if (value) {
    alertFormatting(action, key, value, regex(key));
  }
};

/**
 * Switch that returns appropriate regex for key
 */
/* eslint-disable complexity */
export const regex = (key) => {
  switch (key) {
    case 'page':
    case 'landing_page':
      return /(https?:\/\/)[.-_0-9a-z]+(\/.*)?/i;
    case 'referer':
      return /(https?)?:\/\/[.-_0-9a-z]+(\/.*)?/i;
    case 'ut_dev_referer':
      return /ut\.dev/i;
    case 'ut_st_referer':
      return /utstaging\.xyz/i;
    case 'ut_pr_referer':
      return /universitytutor\.com/i;
    case 'visitor_id':
      return /[0-9a-z]{8}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{12}/i;
    case 'us_phone':
      return /(^(\+1)?[2-9]\d{9}$)/;
    case 'intl_phone':
      return /(^(\+?[0-9]{8,15})$)/;
    case 'us_zip_code':
      return /^[0-9]{5}(?:-[0-9]{4})?$/;
    case 'ca_zip_code':
      return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
    case 'gb_zip_code':
      return new RegExp(
        '^([Gg][Ii][Rr] 0[Aa]{2})|' +
        '((([A-Za-z][0-9]{1,2})|' +
        '(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|' +
        '(([A-Za-z][0-9][A-Za-z])|'+
        '([A-Za-z][A-Ha-hJ-Yj-y][0-9]?' +
        '[A-Za-z]))))[ ]{0,1}[0-9][A-Za-z]{2})$'
      );
    case 'intl_zip_code':
      return /^([\w- ]){4,10}$/i;
    case 'email':
      return /(.+)@(.+)\.([a-z]{2,5})/i;
    case 'first_name':
    case 'last_name':
      return /.*\S+.*/;
    case 'full_name':
      return /.*\S+\s\S+.*/;
    default:
      return /\S+/;
  }
};
/* eslint-enable complexity */

export const alertMissing = (action, key) => {
  trackWarn('lead-form', { 'error': 'Form Wizard Submit Error: Required key value missing',
                           'action': action,
                           'key': key });
  if (!window.newrelic) {
    return;
  }
  const errorMsg = `Form Wizard Validation Error (missing): ${action}, ${key}`;
  const dataAttributes = { 'message': `${action}: ${key} has no value`,
                           'error-type': 'missing',
                           'action': action,
                           'key': key,
                           'page': window.location.href };
  window.newrelic.noticeError(new Error(errorMsg), { 'customAttributes': dataAttributes });
};

export const alertFormatting = (action, key, value, regex) => {
  if (value && !regex.test(value)) {
    trackWarn('lead-form', { 'error': 'Form Wizard Submit Error: Value is not formatted properly',
                             'action': action,
                             'key': key,
                             'value': value });
    if (!window.newrelic) {
      return;
    }
    const errorMsg = `Form Wizard Validation Error (formatting): ${action}, ${key}: ${value}`;
    const dataAttributes = { 'message': `${action}: ${key}:${value} is not formatted properly`,
                             'error-type': 'formatting',
                             'action': action,
                             'key': key,
                             'value': value,
                             'regex': regex,
                             'page': window.location.href };
    window.newrelic.noticeError(new Error(errorMsg), { 'customAttributes': dataAttributes });
  }
};

export const alertFieldValue = (action, key, value) => {
  if (value) {
    trackWarn('lead-form', { 'error': 'Form Wizard Submit Error: Field Value is not supported',
                             'action': action,
                             'key': key,
                             'value': value });
    if (!window.newrelic) {
      return;
    }
    const errorMsg = `Form Wizard Field Validation Error (value): ${action}, ${key}: ${value}`;
    const dataAttributes = { 'message': `${action}: ${key}:${value} is not a supported value`,
                             'error-type': 'field-value',
                             'action': action,
                             'key': key,
                             'value': value,
                             'page': window.location.href };
    window.newrelic.noticeError(new Error(errorMsg), { 'customAttributes': dataAttributes });
  }
};

/*
Alert submit error when two or more fields values are not valid together
@param {String} action - Action name
@param {Array} keys - Array with field keys
@param {Array} values - Array with field values
*/
export const alertMultiFieldValue = (action, {keys, values}) => {
  if (keys && values && keys.length === values.length) {
    trackWarn('lead-form', { 'error': 'Form Wizard Submit Error: Field Value is not supported',
                             'action': action,
                             'key': keys,
                             'value': values });
    if (!window.newrelic) {
      return;
    }

    const keysString = keys.join(', ');

    const mappedKeyValues = keys.reduce((output, key, index) => {
      var endSeparator = values[index + 1] ? ', ' : '';
      return output + `${key}: ${values[index]}${endSeparator}`;
    }, '');

    const errorMsg = `Form Wizard Field Validation Error (multi field error): ${action}, ${keysString}`;
    const dataAttributes = { 'message': `${action}: ${mappedKeyValues} are not valid together`,
                             'error-type': 'multi-field-value',
                             'action': action,
                             'keys': keysString,
                             'values': values.join(', '),
                             'page': window.location.href };
    window.newrelic.noticeError(new Error(errorMsg), { 'customAttributes': dataAttributes });
  }
};
